import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Button, Input, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from '@cargomatic/ui';
import { DEFAULT_DISTANCE_UNIT, DEFAULT_WEIGHT_UNIT, } from 'utils/localization/types';
const EPSILON = 0.00001;
const isFloatLessThan = (a, b) => b - a > (Math.abs(a) < Math.abs(b) ? Math.abs(b) : Math.abs(a)) * EPSILON;
function dataFromTariff(tariff) {
    var _a, _b;
    if (!tariff)
        return { zonesList: [], rows: [] };
    const zonesList = (tariff.zones || []).map((z) => ({
        minDistance: z[0],
        maxDistance: z[1],
    }));
    const rows = [];
    const count = tariff.load_ranges ? tariff.load_ranges.length : 0;
    for (let i = 0; i < count; ++i) {
        const pricesList = (tariff.zone_to_load || []).map((col) => i < col.length ? col[i] : 0);
        const loadR = ((_a = tariff.load_ranges) === null || _a === void 0 ? void 0 : _a[i]) || [0, 0];
        const weightR = ((_b = tariff.weight_ranges) === null || _b === void 0 ? void 0 : _b[i]) || [0, 0];
        rows.push({
            loadRange: { minPallets: loadR[0], maxPallets: loadR[1] },
            weightRange: { minWeight: weightR[0], maxWeight: weightR[1] },
            pricesList,
        });
    }
    return { zonesList, rows };
}
const loadRangesOf = (d) => d.rows.map((r) => [r.loadRange.minPallets, r.loadRange.maxPallets]);
const weightRangesOf = (d) => d.rows.map((r) => [r.weightRange.minWeight, r.weightRange.maxWeight]);
const zonesOf = (d) => d.zonesList.map((z) => [z.minDistance, z.maxDistance]);
const zoneToLoadOf = (d) => {
    const out = [];
    d.rows.forEach((row, rowIdx) => {
        row.pricesList.forEach((price, priceIdx) => {
            if (!out[priceIdx])
                out[priceIdx] = [];
            out[priceIdx][rowIdx] = price;
        });
    });
    return out;
};
function buildCsv(_tariffName, d, t) {
    let csv = 'data:text/csv;charset=utf-8,';
    let line = `${t('LOAD_RANGE')},${t('WEIGHT_RANGE')},`;
    d.zonesList.forEach((zone, idx) => {
        line += `${t('ZONE_NUMBER_DISTANCE', {
            zoneIndex: idx + 1,
            distance: `${zone.minDistance}-${zone.maxDistance}`,
        })},`;
    });
    csv += `${line}\r\n`;
    d.rows.forEach((row) => {
        let rowLine = `${row.loadRange.minPallets}-${row.loadRange.maxPallets},`;
        rowLine += `${row.weightRange.minWeight}-${row.weightRange.maxWeight},`;
        row.pricesList.forEach((price) => {
            rowLine += `${price},`;
        });
        csv += `${rowLine}\r\n`;
    });
    return encodeURI(csv);
}
function ValidatedCell({ value, invalid, warning, onClick, }) {
    const className = invalid
        ? 'value-invalid cursor-pointer'
        : warning
            ? 'value-warning cursor-pointer'
            : 'cursor-pointer';
    const inner = (React.createElement("button", { type: "button", className: `${className} bg-transparent border-0 p-0`, onClick: onClick }, value));
    if (warning && !invalid) {
        return (React.createElement(TooltipProvider, null,
            React.createElement(Tooltip, null,
                React.createElement(TooltipTrigger, { asChild: true }, inner),
                React.createElement(TooltipContent, null, warning))));
    }
    return inner;
}
function InlineNumberEditor({ initial, onCommit, onCancel, }) {
    const [value, setValue] = useState(String(initial !== null && initial !== void 0 ? initial : ''));
    const ref = useRef(null);
    useEffect(() => {
        var _a, _b;
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        (_b = ref.current) === null || _b === void 0 ? void 0 : _b.select();
    }, []);
    return (React.createElement(Input, { ref: ref, type: "number", value: value, onChange: (e) => setValue(e.target.value), onKeyUp: (e) => {
            if (e.key === 'Enter')
                onCommit(parseFloat(value));
            if (e.key === 'Escape')
                onCancel();
        }, onBlur: () => onCommit(parseFloat(value)), className: "h-8" }));
}
export function TariffPricesTable({ tariff, onChange }) {
    const { t } = useTranslation();
    const [data, setData] = useState(() => dataFromTariff(tariff));
    const [editing, setEditing] = useState(null);
    // Re-seed when the tariff prop changes (e.g. clone or after a save).
    const seededIdRef = useRef();
    useEffect(() => {
        const sig = JSON.stringify({
            z: tariff === null || tariff === void 0 ? void 0 : tariff.zones,
            l: tariff === null || tariff === void 0 ? void 0 : tariff.load_ranges,
            w: tariff === null || tariff === void 0 ? void 0 : tariff.weight_ranges,
            p: tariff === null || tariff === void 0 ? void 0 : tariff.zone_to_load,
        });
        if (sig !== seededIdRef.current) {
            setData(dataFromTariff(tariff));
            seededIdRef.current = sig;
        }
    }, [tariff]);
    const emit = useCallback((changed) => onChange === null || onChange === void 0 ? void 0 : onChange(changed), [onChange]);
    const stopEdit = useCallback(() => setEditing(null), []);
    // --- Mutations -----------------------------------------------------------
    const addZone = () => {
        setData((prev) => {
            const next = {
                zonesList: [...prev.zonesList, { minDistance: 0, maxDistance: 0 }],
                rows: prev.rows.map((r) => (Object.assign(Object.assign({}, r), { pricesList: [...r.pricesList, 0] }))),
            };
            emit({ zones: zonesOf(next), zone_to_load: zoneToLoadOf(next) });
            return next;
        });
    };
    const removeZone = (idx) => {
        setData((prev) => {
            const zonesList = prev.zonesList.filter((_, i) => i !== idx);
            const rows = prev.rows.map((r) => (Object.assign(Object.assign({}, r), { pricesList: r.pricesList.filter((_, i) => i !== idx) })));
            const next = { zonesList, rows };
            emit({ zones: zonesOf(next), zone_to_load: zoneToLoadOf(next) });
            return next;
        });
    };
    const addRow = () => {
        setData((prev) => {
            const last = prev.rows[prev.rows.length - 1];
            const newRow = last
                ? JSON.parse(JSON.stringify(last))
                : {
                    loadRange: { minPallets: 0, maxPallets: 0 },
                    weightRange: { minWeight: 0, maxWeight: 0 },
                    pricesList: prev.zonesList.map(() => 0),
                };
            const rows = [...prev.rows, newRow];
            const next = Object.assign(Object.assign({}, prev), { rows });
            emit({
                load_ranges: loadRangesOf(next),
                weight_ranges: weightRangesOf(next),
                zone_to_load: zoneToLoadOf(next),
            });
            return next;
        });
    };
    const insertRow = (atIndex) => {
        setData((prev) => {
            if (atIndex > prev.rows.length)
                return prev;
            const seed = prev.rows[atIndex - 1];
            const newRow = seed
                ? JSON.parse(JSON.stringify(seed))
                : {
                    loadRange: { minPallets: 0, maxPallets: 0 },
                    weightRange: { minWeight: 0, maxWeight: 0 },
                    pricesList: prev.zonesList.map(() => 0),
                };
            const rows = [...prev.rows];
            rows.splice(atIndex, 0, newRow);
            const next = Object.assign(Object.assign({}, prev), { rows });
            emit({
                load_ranges: loadRangesOf(next),
                weight_ranges: weightRangesOf(next),
                zone_to_load: zoneToLoadOf(next),
            });
            return next;
        });
    };
    const removeRow = (idx) => {
        setData((prev) => {
            const rows = prev.rows.filter((_, i) => i !== idx);
            const next = Object.assign(Object.assign({}, prev), { rows });
            emit({
                load_ranges: loadRangesOf(next),
                weight_ranges: weightRangesOf(next),
                zone_to_load: zoneToLoadOf(next),
            });
            return next;
        });
    };
    const commitEdit = (target, raw) => {
        const value = Number.isFinite(raw) ? raw : 0;
        setData((prev) => {
            const next = {
                zonesList: prev.zonesList.map((z) => (Object.assign({}, z))),
                rows: prev.rows.map((r) => (Object.assign(Object.assign({}, r), { loadRange: Object.assign({}, r.loadRange), weightRange: Object.assign({}, r.weightRange), pricesList: [...r.pricesList] }))),
            };
            switch (target.kind) {
                case 'load':
                    next.rows[target.rowIndex].loadRange[target.key] = value;
                    emit({ load_ranges: loadRangesOf(next) });
                    break;
                case 'weight':
                    next.rows[target.rowIndex].weightRange[target.key] = value;
                    emit({ weight_ranges: weightRangesOf(next) });
                    break;
                case 'price':
                    next.rows[target.rowIndex].pricesList[target.zoneIndex] = parseFloat(value.toFixed(2));
                    emit({ zone_to_load: zoneToLoadOf(next) });
                    break;
                case 'zone':
                    next.zonesList[target.zoneIndex][target.key] = parseInt(String(value), 10);
                    emit({ zones: zonesOf(next) });
                    break;
            }
            return next;
        });
        setEditing(null);
    };
    // --- Validation helpers --------------------------------------------------
    const priceWarning = (rowIndex, zoneIndex) => {
        var _a, _b;
        const v = (_b = (_a = data.rows[rowIndex]) === null || _a === void 0 ? void 0 : _a.pricesList[zoneIndex]) !== null && _b !== void 0 ? _b : 0;
        if (zoneIndex > 0) {
            const prevColPrice = data.rows[rowIndex].pricesList[zoneIndex - 1];
            if (isFloatLessThan(v, prevColPrice)) {
                return t('SHOULD_BE_GTE_PREVIOUS_ZONE', 'Should be ≥ previous zone');
            }
        }
        if (rowIndex > 0) {
            const prevRowPrice = data.rows[rowIndex - 1].pricesList[zoneIndex];
            if (isFloatLessThan(v, prevRowPrice)) {
                return t('SHOULD_BE_GTE_PREVIOUS_ROW', 'Should be ≥ previous row');
            }
        }
        return null;
    };
    const priceInvalid = (rowIndex, zoneIndex) => {
        var _a, _b;
        const v = (_b = (_a = data.rows[rowIndex]) === null || _a === void 0 ? void 0 : _a.pricesList[zoneIndex]) !== null && _b !== void 0 ? _b : 0;
        return !(v > 0);
    };
    const zoneInvalid = (zone, zoneIndex, key) => {
        const v = zone[key];
        if (key === 'maxDistance') {
            return !(v > 0 && v >= zone.minDistance);
        }
        return !(v > 0 || zoneIndex === 0);
    };
    const isEditingTarget = (a, b) => {
        if (!b)
            return false;
        if (a.kind !== b.kind)
            return false;
        if (a.kind === 'price' && b.kind === 'price')
            return a.rowIndex === b.rowIndex && a.zoneIndex === b.zoneIndex;
        if (a.kind === 'zone' && b.kind === 'zone')
            return a.zoneIndex === b.zoneIndex && a.key === b.key;
        if ((a.kind === 'load' || a.kind === 'weight') && a.kind === b.kind)
            return a.rowIndex === b.rowIndex && a.key === b.key;
        return false;
    };
    const csvHref = useMemo(() => buildCsv((tariff === null || tariff === void 0 ? void 0 : tariff.name) || 'tariff', data, t), [data, tariff === null || tariff === void 0 ? void 0 : tariff.name, t]);
    const csvFilename = `${(tariff === null || tariff === void 0 ? void 0 : tariff.name) || 'tariff'}.csv`;
    const totalCols = 2 + data.zonesList.length + 1;
    return (React.createElement("div", { className: "overflow-x-auto" },
        React.createElement(Table, { className: "whitespace-nowrap" },
            React.createElement(TableHeader, null,
                React.createElement(TableRow, null,
                    React.createElement(TableHead, null, t('LOAD_RANGE_PALLETS')),
                    React.createElement(TableHead, null, t('WEIGHT_RANGE_WITH_UNIT', {
                        weight_units: DEFAULT_WEIGHT_UNIT,
                    })),
                    data.zonesList.map((zone, zoneIdx) => (React.createElement(TableHead, { key: `zone-h-${zoneIdx}` },
                        React.createElement("div", { className: "space-y-1" },
                            React.createElement("div", { className: "flex items-center gap-2" },
                                React.createElement("span", null, t('ZONE_NUMBER_WITH_DISTANCE_UNIT', {
                                    number: zoneIdx + 1,
                                    distanceUnit: DEFAULT_DISTANCE_UNIT,
                                })),
                                React.createElement(Button, { variant: "ghost", size: "sm", className: "h-6 w-6 p-0 text-destructive", onClick: () => removeZone(zoneIdx), "aria-label": "Remove zone" },
                                    React.createElement(X, { className: "h-3 w-3" }))),
                            React.createElement("div", { className: "flex items-center gap-1" }, ['minDistance', 'maxDistance'].map((key) => {
                                const target = {
                                    kind: 'zone',
                                    zoneIndex: zoneIdx,
                                    key,
                                };
                                const isEditing = isEditingTarget(target, editing);
                                return (React.createElement("div", { key: key, className: "w-20" }, isEditing ? (React.createElement(InlineNumberEditor, { initial: zone[key], onCommit: (v) => commitEdit(target, v), onCancel: stopEdit })) : (React.createElement(ValidatedCell, { value: zone[key], invalid: zoneInvalid(zone, zoneIdx, key), onClick: () => setEditing(target) }))));
                            })))))),
                    React.createElement(TableHead, { className: "text-right" },
                        React.createElement(Button, { variant: "outline", size: "sm", onClick: addZone, disabled: data.rows.length === 0 },
                            React.createElement(Plus, { className: "h-3 w-3 mr-1" }),
                            t('ADD_ZONE'))))),
            React.createElement(TableBody, null, data.rows.map((row, rowIdx) => (React.createElement(TableRow, { key: rowIdx },
                React.createElement(TableCell, null,
                    React.createElement("div", { className: "flex items-center gap-1" }, ['minPallets', 'maxPallets'].map((key) => {
                        const target = {
                            kind: 'load',
                            rowIndex: rowIdx,
                            key,
                        };
                        const isEditing = isEditingTarget(target, editing);
                        return (React.createElement("div", { key: key, className: "w-20" }, isEditing ? (React.createElement(InlineNumberEditor, { initial: row.loadRange[key], onCommit: (v) => commitEdit(target, v), onCancel: stopEdit })) : (React.createElement(ValidatedCell, { value: row.loadRange[key], onClick: () => setEditing(target) }))));
                    }))),
                React.createElement(TableCell, null,
                    React.createElement("div", { className: "flex items-center gap-1" }, ['minWeight', 'maxWeight'].map((key) => {
                        const target = {
                            kind: 'weight',
                            rowIndex: rowIdx,
                            key,
                        };
                        const isEditing = isEditingTarget(target, editing);
                        return (React.createElement("div", { key: key, className: "w-20" }, isEditing ? (React.createElement(InlineNumberEditor, { initial: row.weightRange[key], onCommit: (v) => commitEdit(target, v), onCancel: stopEdit })) : (React.createElement(ValidatedCell, { value: row.weightRange[key], onClick: () => setEditing(target) }))));
                    }))),
                row.pricesList.map((price, zoneIdx) => {
                    const target = {
                        kind: 'price',
                        rowIndex: rowIdx,
                        zoneIndex: zoneIdx,
                    };
                    const isEditing = isEditingTarget(target, editing);
                    return (React.createElement(TableCell, { key: zoneIdx }, isEditing ? (React.createElement("div", { className: "w-24" },
                        React.createElement(InlineNumberEditor, { initial: price, onCommit: (v) => commitEdit(target, v), onCancel: stopEdit }))) : (React.createElement(ValidatedCell, { value: (price !== null && price !== void 0 ? price : 0).toFixed(2), invalid: priceInvalid(rowIdx, zoneIdx), warning: priceWarning(rowIdx, zoneIdx) || undefined, onClick: () => setEditing(target) }))));
                }),
                React.createElement(TableCell, { className: "text-right" },
                    React.createElement("div", { className: "flex items-center justify-end gap-1" },
                        React.createElement(Button, { variant: "ghost", size: "sm", className: "h-7 w-7 p-0", onClick: () => insertRow(rowIdx + 1), "aria-label": "Insert row" },
                            React.createElement(Plus, { className: "h-3 w-3" })),
                        React.createElement(Button, { variant: "ghost", size: "sm", className: "h-7 w-7 p-0 text-destructive", onClick: () => removeRow(rowIdx), "aria-label": "Remove row" },
                            React.createElement(X, { className: "h-3 w-3" })))))))),
            React.createElement("tfoot", null,
                React.createElement("tr", null,
                    React.createElement("td", { colSpan: 1, className: "p-2" },
                        React.createElement(Button, { variant: "outline", size: "sm", onClick: addRow },
                            React.createElement(Plus, { className: "h-3 w-3 mr-1" }),
                            t('ADD_RANGE_ROW'))),
                    React.createElement("td", { colSpan: Math.max(totalCols - 2, 0) }),
                    React.createElement("td", { className: "p-2 text-right" },
                        React.createElement("a", { href: csvHref, download: csvFilename, target: "_blank", rel: "noreferrer", className: "text-primary hover:underline" }, t('EXPORT_TO_CSV'))))))));
}
export default TariffPricesTable;
